import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Linkedin, Mail, MapPin, Phone, Shield, Globe, CheckCircle, Lock } from 'lucide-react';
import logo from '../Logo.png';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#0a1f14] text-gray-300">
      {/* Trust bar */}
      <div className="border-b border-white/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-400">
            {[
              { icon: <Lock className="w-4 h-4" />, label: 'SOC 2 Compliant' },
              { icon: <Shield className="w-4 h-4" />, label: 'Bank-Level Encryption' },
              { icon: <Globe className="w-4 h-4" />, label: '50-State Coverage' },
              { icon: <CheckCircle className="w-4 h-4" />, label: '99.9% Uptime SLA' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-emerald-400">{item.icon}</span>
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main footer content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-10">

          {/* Brand — spans 2 cols on large */}
          <div className="lg:col-span-2 space-y-5">
            <img src={logo} alt="Verdionix" className="h-14 w-auto rounded-xl shadow-md" />
            <p className="text-sm text-gray-400 leading-relaxed max-w-xs">
              The all-in-one cannabis compliance platform. Directory, compliance management, and AI-powered industry intelligence — built for operators at every scale.
            </p>
            <p className="text-xs font-semibold text-emerald-400 uppercase tracking-widest">
              Stay compliant. Stay ahead.
            </p>
            <div className="flex gap-3 pt-1">
              {[
                { href: '#', icon: <Facebook className="w-4 h-4" />, label: 'Facebook' },
                { href: '#', icon: <Twitter className="w-4 h-4" />, label: 'Twitter' },
                { href: '#', icon: <Linkedin className="w-4 h-4" />, label: 'LinkedIn' },
                { href: 'mailto:contact@verdionix.com', icon: <Mail className="w-4 h-4" />, label: 'Email' },
              ].map((s, i) => (
                <a
                  key={i}
                  href={s.href}
                  aria-label={s.label}
                  className="w-9 h-9 rounded-lg bg-white/10 hover:bg-emerald-600 transition-colors flex items-center justify-center text-gray-300 hover:text-white"
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Product */}
          <div>
            <h3 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">Product</h3>
            <ul className="space-y-3">
              {[
                { label: 'Features', to: '/#features' },
                { label: 'Pricing', to: '/#pricing' },
                { label: 'Data Research', to: '/#features' },
                { label: 'AI Insights', to: '/#features' },
                { label: 'Multi-State', to: '/#features' },
              ].map((l, i) => (
                <li key={i}>
                  <Link to={l.to} className="text-sm text-gray-400 hover:text-emerald-400 transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">Company</h3>
            <ul className="space-y-3">
              {[
                { label: 'About Us', href: '#' },
                { label: 'Careers', href: '#' },
                { label: 'Blog', href: '#' },
                { label: 'Privacy Policy', href: '#' },
                { label: 'Terms of Service', href: '#' },
              ].map((l, i) => (
                <li key={i}>
                  <a href={l.href} className="text-sm text-gray-400 hover:text-emerald-400 transition-colors">
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">Get In Touch</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-sm text-gray-400">
                <Mail className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                <a href="mailto:contact@verdionix.com" className="hover:text-emerald-400 transition-colors break-all">
                  contact@verdionix.com
                </a>
              </li>
              <li className="flex items-start gap-3 text-sm text-gray-400">
                <Phone className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                <span>+1 (800) 000-0000</span>
              </li>
              <li className="flex items-start gap-3 text-sm text-gray-400">
                <MapPin className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                <span>United States</span>
              </li>
            </ul>
            <div className="mt-6">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
              >
                Create Account
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 text-sm text-gray-500">
            <p>© {currentYear} Verdionix. All rights reserved.</p>
            <p className="text-emerald-500 font-medium text-xs tracking-wide uppercase">Compliance made simple. Growth made possible.</p>
            <div className="flex gap-5">
              {['Privacy', 'Terms', 'Cookies'].map(l => (
                <a key={l} href="#" className="hover:text-gray-300 transition-colors">{l}</a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
